import { getBrowserLocalStorage } from '@instamenu/browser-storage';
import { useState, useCallback, useEffect } from 'react';

interface UseSidebarResizeOptions {
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
}

export function useSidebarResize({
  minWidth = 260,
  maxWidth = 420,
  defaultWidth = 360,
}: UseSidebarResizeOptions = {}) {
  const [browserLocalStorage] = useState(() => getBrowserLocalStorage());
  const [isResizing, setIsResizing] = useState(false);
  const [width, setWidth] = useState(() => {
    try {
      const savedWidth = browserLocalStorage?.getItem('sidebarWidth');
      if (!savedWidth) return defaultWidth;

      const parsedWidth = parseInt(savedWidth, 10);
      return isNaN(parsedWidth) ? defaultWidth : parsedWidth;
    } catch {
      return defaultWidth;
    }
  });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    browserLocalStorage?.setItem('sidebarWidth', width.toString());
  }, [browserLocalStorage, width]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = e.clientX < minWidth ? 0 : Math.min(e.clientX, maxWidth);
      setWidth(newWidth);
    },
    [isResizing, minWidth, maxWidth],
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return {
    width,
    setWidth,
    isResizing,
    handleMouseDown,
  };
}
